import React from "react";
import type { SyncPhase, SyncStatus } from "../services/offline/interfaces";

export type SyncSource = "catalog-refresh" | null;

export interface SyncProgressState {
  phase: SyncPhase | undefined;
  status: SyncStatus;
  recordCounts: Record<string, number>;
  error: string | null;
  infoMessage: string | null;
  isSyncing: boolean;
  syncSource: SyncSource;
}

interface SyncProgressContextValue extends SyncProgressState {
  setStatus: (status: SyncStatus, message?: string) => void;
  setProgress: (phase: SyncPhase, entityType: string, recordCount: number) => void;
  setSyncSource: (source: SyncSource) => void;
  reset: () => void;
  registerRetry: (fn: () => void) => void;
  triggerRetry: () => void;
  /** Registrato da `useOfflineSync` per permettere a «Salta» di abortire la richiesta in corso. */
  setRunningAbortHandler: (handler: (() => void) | undefined) => void;
  abortRunningSync: () => void;
}

const initialState: SyncProgressState = {
  phase: undefined,
  status: "idle",
  recordCounts: {},
  error: null,
  infoMessage: null,
  isSyncing: false,
  syncSource: null,
};

const SyncProgressContext = React.createContext<SyncProgressContextValue | null>(null);

export function SyncProgressProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<SyncProgressState>(initialState);
  const retryRef = React.useRef<(() => void) | null>(null);
  const runningAbortHandlerRef = React.useRef<(() => void) | undefined>(undefined);

  const registerRetry = React.useCallback((fn: () => void) => {
    retryRef.current = fn;
  }, []);

  const triggerRetry = React.useCallback(() => {
    retryRef.current?.();
  }, []);

  const setStatus = React.useCallback((status: SyncStatus, message?: string) => {
    setState((prev) => {
      const infoMessage =
        status === "skipped"
          ? message ?? "Sincronizzazione non eseguita."
          : message !== undefined
            ? message
            : prev.infoMessage;
      const next: SyncProgressState = {
        ...prev,
        status,
        error: status === "error" ? message ?? prev.error : null,
        infoMessage,
        isSyncing: status === "phase1" || status === "phase2",
      };
      if (status === "phase1") next.phase = 1;
      if (status === "phase2") next.phase = 2;
      if (status === "done" || status === "error" || status === "skipped" || status === "idle") {
        next.phase = undefined;
      }
      if (status === "done" || status === "error" || status === "idle") {
        next.infoMessage = null;
      }
      return next;
    });
  }, []);

  const setRunningAbortHandler = React.useCallback((handler: (() => void) | undefined) => {
    runningAbortHandlerRef.current = handler;
  }, []);

  const abortRunningSync = React.useCallback(() => {
    runningAbortHandlerRef.current?.();
  }, []);

  const setProgress = React.useCallback((phase: SyncPhase, entityType: string, recordCount: number) => {
    setState((prev) => ({
      ...prev,
      recordCounts: { ...prev.recordCounts, [entityType]: recordCount },
    }));
  }, []);

  const setSyncSource = React.useCallback((syncSource: SyncSource) => {
    setState((prev) => ({ ...prev, syncSource }));
  }, []);

  const reset = React.useCallback(() => {
    setState(initialState);
  }, []);

  const value: SyncProgressContextValue = React.useMemo(() => ({
    ...state,
    setStatus,
    setProgress,
    setSyncSource,
    reset,
    registerRetry,
    triggerRetry,
    setRunningAbortHandler,
    abortRunningSync,
  }), [state, setStatus, setProgress, setSyncSource, reset, registerRetry, triggerRetry, setRunningAbortHandler, abortRunningSync]);

  return (
    <SyncProgressContext.Provider value={value}>
      {children}
    </SyncProgressContext.Provider>
  );
}

export function useSyncProgress(): SyncProgressContextValue {
  const ctx = React.useContext(SyncProgressContext);
  if (!ctx) {
    throw new Error("useSyncProgress must be used within SyncProgressProvider");
  }
  return ctx;
}

export function useSyncProgressOptional(): SyncProgressContextValue | null {
  return React.useContext(SyncProgressContext);
}
